import type { BIResponse, MetricOption } from '@/entities/bi'
import type { FilterSchema } from '@/shared/components/filter/validation'
import type { WidgetPeriods } from '../types'

import React, { useState } from 'react'
import { formatMetricValue, useMoneyMetrics } from '@/pages/dashboard/lib/money'
import { useWidgetParams } from '@/pages/dashboard/lib/useWidgetParams'
import { any, isNil, last, prop } from 'lodash/fp'
import { CheckIcon, ChevronsUpDownIcon, XCircleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	DateAggregation,
	filterPricingAndBusinessMetrics,
	formatMetrics,
	sanitizeResponseBySelectedMetrics,
	useBIAnalyticQuery,
	useMetricsQuery,
	zipSeries,
} from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import { Button } from '@/shared/components/ui/button'
import {
	Card,
	CardAction,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/shared/components/ui/card'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from '@/shared/components/ui/command'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
} from '@/shared/components/ui/empty'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Spinner } from '@/shared/components/ui/spinner'
import {
	ToggleGroup,
	ToggleGroupItem,
} from '@/shared/components/ui/toggle-group'
import { useDisclosure } from '@/shared/hooks/use-disclosure'
import { ampli } from '@/shared/lib/ampli'
import { cn } from '@/shared/lib/utils'
import { NumberFormats } from '@/shared/tools/locale'

import { MainDashboardFilter } from '../../Filter'
import PercentDifferenceBadge from '../../PercentDifferenceBadge'
import { MultiPeriodsSelect } from '../../Periods'
import { WidgetErrorFallback } from '../../WidgetErrorFallback'
import Chart from './Chart'

export default function BusinessMetricsWidget({
	commonPeriods,
	commonFilters,
}: {
	commonPeriods: WidgetPeriods
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const { params, onChangeParams } = useWidgetParams()
	const [aggregation, setAggregation] = useState<DateAggregation>(
		DateAggregation.DateKey
	)
	const [mainMetric, setMainMetric] = useState<string>('revenue')
	const [additionalMetrics, setAdditionalMetrics] = useState<string[]>([
		'sales_items',
		'gross_profit_margin',
	])
	const [periods, setPeriods] = useState<WidgetPeriods | null>(commonPeriods)
	const defaultPeriods = commonPeriods
	const filters = [...commonFilters, ...(params.filters ?? [])]
	const { data: metrics, ...metricsQuery } = useMetricsQuery<MetricOption[]>({
		placeholderData: [],
		select: (data) => formatMetrics(filterPricingAndBusinessMetrics(data), t),
	})
	const currency = useSelectedCurrency()
	const moneyMetrics = useMoneyMetrics()
	const commonQueryParams = {
		currency,
		label: 'BusinessMetrics',
		filters,
		metrics: [...(metrics || []).map(prop('value'))] as string[],
		dimensions: [],
		date_aggregation: aggregation,
	}
	const commonQueryConfig = {
		enabled: !!metrics?.length,
		select: (res: BIResponse | null) =>
			res &&
			sanitizeResponseBySelectedMetrics(res, [
				mainMetric,
				...additionalMetrics,
			]),
	}
	const mainSummaryQuery = useBIAnalyticQuery(
		{
			...commonPeriods.main,
			...(periods?.main || {}),
			...commonQueryParams,
			date_aggregation: null,
		},
		commonQueryConfig
	)
	const comparedSummaryQuery = useBIAnalyticQuery(
		{
			...commonPeriods.compared,
			...(periods?.compared || {}),
			...commonQueryParams,
			date_aggregation: null,
		},
		commonQueryConfig
	)
	const mainPeriodQuery = useBIAnalyticQuery(
		{
			...commonPeriods.main,
			...(periods?.main || {}),
			...commonQueryParams,
		},
		commonQueryConfig
	)
	const comparedPeriodQuery = useBIAnalyticQuery(
		{
			...commonPeriods.compared,
			...(periods?.compared || {}),
			...commonQueryParams,
		},
		commonQueryConfig
	)
	const isLoading = any(prop('isLoading'), [
		metricsQuery,
		mainPeriodQuery,
		comparedPeriodQuery,
		mainSummaryQuery,
		comparedSummaryQuery,
	])
	const isError = any(prop('isError'), [
		metricsQuery,
		mainPeriodQuery,
		comparedPeriodQuery,
		mainSummaryQuery,
		comparedSummaryQuery,
	])

	const lastPoints = getLastPoints(
		mainSummaryQuery.data,
		comparedSummaryQuery.data
	)
	const handleResetAddtitionalMetrics = () => setAdditionalMetrics([])
	const orderedMetrics: MetricOption[] = metrics || []
	const orderedAdditionalMetrics = orderedMetrics
		.filter((metric) => additionalMetrics.includes(metric.value))
		.map((metric) => metric.value)

	return (
		<Card>
			<CardHeader>
				<CardTitle>{t('widget.business_metrics.title')} </CardTitle>
				<CardDescription>
					{t('widget.business_metrics.tooltip')}
				</CardDescription>
				<CardAction>
					<MultiPeriodsSelect
						periods={periods!}
						defaultPeriods={defaultPeriods}
						onChangePeriods={setPeriods}
					/>
				</CardAction>
			</CardHeader>
			<CardContent>
				<div className='flex min-h-13 justify-between'>
					<div className='flex items-center gap-2'>
						<MainMetricDropDown
							options={orderedMetrics.filter(
								(metric) => !additionalMetrics.includes(metric.value)
							)}
							value={mainMetric}
							onChange={(value) => {
								ampli.dashboardBiMainMetricChoiceForHistoricalGraph({
									value,
								})
								setMainMetric(value)
							}}
						/>
						<AdditionalMetricsDropDown
							options={orderedMetrics.filter(
								(metric) => mainMetric !== metric.value
							)}
							values={additionalMetrics}
							onClear={handleResetAddtitionalMetrics}
							onChange={(values: string[]) => {
								ampli.dashboardBiAdditionalMetricsChoiseForHistoricalGraph({
									metric: values,
								})
								setAdditionalMetrics(values)
							}}
						/>
						<MainDashboardFilter
							irremovableCount={commonFilters.length}
							filters={filters || []}
							onChange={(filters) => {
								onChangeParams({ ...params, filters })
							}}
						/>
					</div>
					<DescriptionList className='flex items-start gap-4'>
						{metrics?.length &&
							[mainMetric, ...orderedAdditionalMetrics.slice(0, 2)].map(
								(key) => {
									const metric = metrics.find((metric) => metric.value === key)!
									const currentValue = Number(lastPoints.current?.[key] || 0)
									const prevValue = Number(lastPoints.compared?.[key] || 0)
									const diffPercent = (currentValue * 100) / prevValue - 100

									return (
										<DescriptionGroup key={key}>
											<DescriptionTerm>{metric.label}</DescriptionTerm>
											<DescriptionDetail>
												{isLoading ? (
													<Skeleton className='h-4 w-10' />
												) : (
													<div className='flex items-center'>
														<h4 className='mr-1 text-2xl font-medium'>
															{isNil(currentValue)
																? '-'
																: formatMetricValue(
																		currentValue,
																		key,
																		currency,
																		moneyMetrics,
																		NumberFormats.Compact
																	)}
														</h4>
														{Number.isFinite(diffPercent) && (
															<div className='flex flex-col'>
																<PercentDifferenceBadge value={diffPercent} />
															</div>
														)}
													</div>
												)}
											</DescriptionDetail>
										</DescriptionGroup>
									)
								}
							)}
					</DescriptionList>
				</div>
				<div className='relative shadow-none'>
					<div className='my-2 flex items-center justify-between'>
						<ToggleGroup
							variant='outline'
							type='single'
							size='sm'
							defaultValue={aggregation}
							value={aggregation}
							onValueChange={setAggregation as (value: string) => void}
							className='*:data-[slot=toggle-group-item]:px-3'
						>
							{[
								DateAggregation.DateKey,
								DateAggregation.WeekKey,
								DateAggregation.MonthKey,
							].map((freqOption) => (
								<ToggleGroupItem
									value={freqOption}
									key={freqOption}
									onClick={() => {
										ampli.dashboardBiSwitchRevenuePeriodInMetricToCompare({
											value: freqOption,
										})
									}}
								>
									{t(`agg_short_${freqOption}`)}
								</ToggleGroupItem>
							))}
						</ToggleGroup>
					</div>
					{isError ? (
						<WidgetErrorFallback className='h-110' />
					) : isLoading ? (
						<Empty className='h-110'>
							<EmptyHeader>
								<EmptyMedia variant='icon'>
									<Spinner />
								</EmptyMedia>
								<EmptyDescription>{t('loading')}</EmptyDescription>
							</EmptyHeader>
						</Empty>
					) : (
						<Chart
							data={mainPeriodQuery.data || { meta: [], data: [] }}
							mainMetric={mainMetric}
							additionalMetrics={additionalMetrics}
							aggregation={aggregation}
							metrics={metrics || []}
						/>
					)}
				</div>
			</CardContent>
		</Card>
	)
}

const getLastPoints = (
	main?: BIResponse | null,
	compared?: BIResponse | null
) => {
	if (!main?.meta || !compared?.meta) {
		return {
			current: null,
			compared: null,
		}
	}

	return {
		current: last(
			zipSeries<Record<string, number>>(main?.meta, [last(main?.data)!])
		),
		compared: last(
			zipSeries<Record<string, number>>(main?.meta, [last(compared?.data)!])
		),
	}
}

function MainMetricDropDown({
	options,
	value,
	className,
	...props
}: {
	className?: string
	value: string
	options: MetricOption[]
	onChange(value: string): void
}) {
	const { t } = useTranslation()

	const popover = useDisclosure()
	const currentValue = options.find(
		(metric: MetricOption) => metric.value === value
	)
	const content = (
		<Command>
			<CommandInput
				placeholder={t('general_search', { defaultValue: 'Search' })}
				className='h-8'
			/>
			<CommandList>
				<CommandEmpty>
					{' '}
					{t('general_no_results', { defaultValue: 'No results found' })}
				</CommandEmpty>
				<CommandGroup>
					{options.map((option) => (
						<CommandItem
							key={option.value}
							value={option.value}
							onSelect={(currentValue) => {
								props.onChange(currentValue)
								popover.close()
							}}
						>
							{option.label}
							<CheckIcon
								className={cn(
									'ml-auto',
									value == option.value ? 'opacity-100' : 'opacity-0'
								)}
							/>
						</CommandItem>
					))}
				</CommandGroup>
			</CommandList>
		</Command>
	)

	return (
		<Popover
			onOpenChange={(open) => (open ? popover.open() : popover.close())}
			open={popover.isOpen}
		>
			<PopoverTrigger asChild>
				<Button className={className}>
					{t('widget.business_metrics.main_metric_title', {
						defaultValue: 'Main metric',
					})}
					{':  '}
					<span className='font-medium'>{currentValue?.label}</span>
					<ChevronsUpDownIcon className='shrink-0 opacity-50' />
				</Button>
			</PopoverTrigger>
			<PopoverContent className='w-auto p-0' side='bottom' align='start'>
				{content}
			</PopoverContent>
		</Popover>
	)
}
function AdditionalMetricsDropDown({
	options,
	values,
	className,
	...props
}: {
	className?: string
	value?: string
	values: string[]
	options: MetricOption[]
	onClear: () => void
	onChange(values: string[]): void
}) {
	const { t } = useTranslation()

	const popover = useDisclosure()
	const content = (
		<Command>
			<CommandInput
				placeholder={t('general_search', { defaultValue: 'Search' })}
				className='h-8'
			/>
			<CommandList className='scroll-pb-12'>
				<CommandEmpty>
					{t('general_no_results', { defaultValue: 'No results found' })}
				</CommandEmpty>
				<CommandGroup>
					{options.map((option) => (
						<CommandItem
							key={option.value}
							value={option.value}
							onSelect={(currentValue) => {
								props.onChange(
									values?.includes(currentValue)
										? values.filter((value) => value !== currentValue)
										: [...values, currentValue]
								)
							}}
						>
							{option.label}
							<CheckIcon
								className={cn(
									'ml-auto',
									values?.includes(option.value) ? 'opacity-100' : 'opacity-0'
								)}
							/>
						</CommandItem>
					))}
				</CommandGroup>
				<CommandSeparator className='sticky bottom-10' />
				<CommandGroup className='sticky bottom-0 bg-popover'>
					<CommandItem onSelect={() => props.onClear()}>
						<XCircleIcon />
						Clear
					</CommandItem>
				</CommandGroup>
			</CommandList>
		</Command>
	)

	return (
		<Popover
			onOpenChange={(open) => (open ? popover.open() : popover.close())}
			open={popover.isOpen}
		>
			<PopoverTrigger asChild>
				<Button className={className}>
					{t('widget.business_metrics.additional_metric_title', {
						defaultValue: 'Additional metrics',
					})}{' '}
					<span className='font-medium'>{`(${values ? values.length : 0})`}</span>
					<ChevronsUpDownIcon className='shrink-0 opacity-50' />
				</Button>
			</PopoverTrigger>
			<PopoverContent className='w-auto p-0' side='bottom' align='start'>
				{content}
			</PopoverContent>
		</Popover>
	)
}
